"use client";

import { useRef, useState } from "react";
import { useEditor } from "@/app/editor/_components/SharedImageScene";
import { useAssetsContext } from "@/contexts/AssetsContext";
import AssetsPanel from "@/app/editor/_components/AssetsPanel";

export default function MyPhotosButton() {
  const [showAssetsPanel, setShowAssetsPanel] = useState(false);
  const assetsPanelRef = useRef<HTMLDivElement>(null);
  const { assets } = useAssetsContext();
  const { originalImage, processedImage, galleryImages, isGalleryEnabled } = useEditor();
  const totalAssetsCount = assets.length;

  return (
    <>
      <button
        onClick={() => setShowAssetsPanel(true)}
        className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${
          showAssetsPanel ? "bg-primary/10 text-primary" : "hover:bg-gray-100 text-gray-600"
        }`}
        title="My Photos"
      >
        {totalAssetsCount > 0 && (
          <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-md">
            {totalAssetsCount > 99 ? "99+" : totalAssetsCount}
          </div>
        )}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="hidden sm:inline">My Photos</span>
      </button>

      <div className="fixed inset-0 pointer-events-none z-[10004]" aria-hidden={!showAssetsPanel}>
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${
            showAssetsPanel ? "opacity-40" : "opacity-0"
          } bg-black z-[10004]`}
          style={{ pointerEvents: showAssetsPanel ? "auto" : "none" }}
          onClick={() => setShowAssetsPanel(false)}
        />
        <div
          ref={assetsPanelRef}
          className="fixed top-0 right-0 h-screen bg-white border-l border-gray-200 shadow-2xl transition-transform duration-300 z-[10005] w-screen md:w-[500px] flex flex-col"
          style={{
            pointerEvents: showAssetsPanel ? "auto" : "none",
            transform: showAssetsPanel ? "translateX(0)" : "translateX(100%)",
          }}
        >
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-gray-900 truncate">My Assets</h3>
            <button
              aria-label="Close"
              onClick={() => setShowAssetsPanel(false)}
              className="text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-2xl"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 overscroll-contain">
            <AssetsPanel
              onUseAsset={(fileId, signedUrl) => {
                (window as any).__uploadedFileId = fileId;
                const event = new CustomEvent("useAssetFile", {
                  detail: { fileId, signedUrl },
                });
                window.dispatchEvent(event);
                setShowAssetsPanel(false);
              }}
              onCloseSidebar={() => setShowAssetsPanel(false)}
              checkReplaceNeeded={async () => {
                const hasImage = !!originalImage;
                const hasGeneratedResults =
                  (isGalleryEnabled && galleryImages && galleryImages.length > 0) || !!processedImage;
                return {
                  needsConfirmation: hasImage && hasGeneratedResults,
                  hasImage,
                  hasGeneratedResults,
                };
              }}
              getCurrentImageFileId={() => {
                return (window as any).__uploadedFileId || null;
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
